/**
 * BackupsSection (F85) — gestión de backups de Postgres desde la UI.
 *
 * Lista archivos del dir `/var/byteisp/backups/`. Acciones:
 *   - Crear backup ahora (pg_dump on-demand cifrado con age)
 *   - Descargar (.dump.age file)
 *   - Borrar
 *
 * v1.0.11: removido el panel de Drive offsite (intento abandonado por
 * políticas org de GCP). Sustituido por placeholder "Backblaze B2 (en
 * construcción)" que se implementa en v1.0.12.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { AlertCircle, Clock, CloudOff, Database, Download, Loader2,
         Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiCall, type ApiError } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { formatDateTime } from "@/lib/utils";

type BackupItem = {
  filename: string;
  size_bytes: number;
  created_at: string;
};

type BackupsListResponse = {
  items: BackupItem[];
  total: number;
  size_total_bytes: number;
  ultimo: BackupItem | null;
};

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function useBackups() {
  return useQuery({
    queryKey: ["backups"],
    queryFn: () => apiCall<BackupsListResponse>("get", "/api/v1/sistema/backups"),
  });
}

// ─── v1.0.11: Placeholder offsite (B2 viene en v1.0.12) ───────────────

function OffsitePlaceholderCard() {
  return (
    <div className="rounded-lg border bg-card p-3 space-y-2 opacity-70">
      <div className="text-[10px] uppercase font-semibold text-muted-foreground flex items-center gap-1">
        <CloudOff size={11} />
        Destino offsite — Backblaze B2 <span className="text-amber-500">(en construcción)</span>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Subida automática de cada backup cifrado a Backblaze B2 (S3-compatible,
        ~10GB gratis, gmail personal funciona — sin Workspace ni políticas
        enterprise). Disponible en próxima versión.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
        <div>
          <Label className="text-[10px] text-muted-foreground">Key ID</Label>
          <Input disabled placeholder="0000abcdef0000000000000a" className="text-xs" />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">App Key</Label>
          <Input disabled placeholder="K000XXXXXXXXXX..." className="text-xs" type="password" />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Bucket</Label>
          <Input disabled placeholder="bytecom-byteisp-backups" className="text-xs" />
        </div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Button size="sm" disabled>Guardar configuración</Button>
        <Button size="sm" variant="outline" disabled>Probar subida ahora</Button>
        <span className="text-[10px] text-muted-foreground italic">
          Mientras tanto, los backups locales cifrados con age siguen funcionando.
        </span>
      </div>
    </div>
  );
}

export function BackupsSection() {
  const qc = useQueryClient();
  const { data, isLoading } = useBackups();
  const [label, setLabel] = useState("manual");

  const crearMut = useMutation({
    mutationFn: () =>
      apiCall<BackupItem>("post", "/api/v1/sistema/backups", { json: { label } }),
    onSuccess: (b) => {
      toast.success(`Backup creado: ${b.filename}`, {
        description: `${fmtSize(b.size_bytes)}`,
      });
      qc.invalidateQueries({ queryKey: ["backups"] });
    },
    onError: (e) => toast.error("No se pudo crear backup", {
      description: (e as ApiError).message,
    }),
  });

  const borrarMut = useMutation({
    mutationFn: (filename: string) =>
      apiCall<{ message: string }>("delete", `/api/v1/sistema/backups/${filename}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backups"] });
      toast.success("Backup eliminado");
    },
    onError: (e) => toast.error("No se pudo eliminar", {
      description: (e as ApiError).message,
    }),
  });

  function descargar(filename: string) {
    // v1.0.10+ fix: el token vive en useAuthStore (Zustand persist con
    // key "byteisp.auth" — JSON-encoded state). Antes leía
    // localStorage.getItem("byteisp.access_token") que NO existe → null →
    // 401 token_invalid. Bug latente desde F85 que nunca se descubrió
    // hasta que Alex intentó descargar en producción.
    const token = useAuthStore.getState().accessToken;
    fetch(`/api/v1/sistema/backups/${filename}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error("No se pudo descargar", { description: e.message }));
  }

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Database size={16} /> Backups de Postgres
          </h3>
          <p className="text-xs text-muted-foreground">
            Snapshot completo en formato `pg_dump -F c` cifrado con age. Se
            ejecuta diario a las 3:00 AM y se conservan 30 días. Puedes crear
            uno ahora antes de un cambio crítico.
          </p>
        </div>
      </div>

      {/* Stats */}
      {data && (
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-lg border bg-card p-3">
            <div className="text-[10px] uppercase text-muted-foreground">Total backups</div>
            <div className="text-2xl font-semibold">{data.total}</div>
          </div>
          <div className="rounded-lg border bg-card p-3">
            <div className="text-[10px] uppercase text-muted-foreground">Espacio usado</div>
            <div className="text-2xl font-semibold">{fmtSize(data.size_total_bytes)}</div>
          </div>
          <div className="rounded-lg border bg-card p-3">
            <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1">
              <Clock size={10} /> Último
            </div>
            <div className="text-xs font-mono mt-1">
              {data.ultimo ? formatDateTime(data.ultimo.created_at) : "Nunca"}
            </div>
          </div>
        </div>
      )}

      {/* Crear backup ahora */}
      <div className="rounded-lg border bg-card p-3">
        <div className="text-[10px] uppercase text-muted-foreground mb-2 font-semibold">
          Crear backup ahora
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-[10px]">Etiqueta (opcional)</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)}
              placeholder="manual / pre-deploy / pre-import" maxLength={40} />
          </div>
          <Button onClick={() => crearMut.mutate()} disabled={crearMut.isPending}>
            {crearMut.isPending ? (
              <><Loader2 size={14} className="animate-spin" /> Creando…</>
            ) : (
              <><Plus size={14} /> Crear backup</>
            )}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">
          ⚠ Bloquea brevemente la API mientras escribe. En DBs grandes puede tardar minutos.
        </p>
      </div>

      {/* v1.0.11: Placeholder de offsite */}
      <OffsitePlaceholderCard />

      {/* Lista */}
      <div className="rounded-lg border bg-card">
        <div className="border-b p-3 text-[10px] uppercase font-semibold text-muted-foreground">
          Histórico
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 size={16} className="animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground flex flex-col items-center gap-2">
            <AlertCircle size={20} className="text-amber-500" />
            <div>Aún no hay backups. Crea el primero arriba o espera al cron de las 3 AM.</div>
          </div>
        ) : (
          <div className="divide-y">
            {items.map((b) => (
              <div key={b.filename} className="flex items-center justify-between gap-2 px-3 py-2">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-mono truncate">{b.filename}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {formatDateTime(b.created_at)} · {fmtSize(b.size_bytes)}
                  </div>
                </div>
                <Button variant="ghost" size="icon"
                  onClick={() => descargar(b.filename)}
                  title="Descargar">
                  <Download size={14} />
                </Button>
                <Button variant="ghost" size="icon"
                  onClick={() => {
                    if (confirm(`¿Borrar backup "${b.filename}"?\nEsta acción no se puede deshacer.`)) {
                      borrarMut.mutate(b.filename);
                    }
                  }}
                  disabled={borrarMut.isPending}
                  title="Borrar">
                  <Trash2 size={14} className="text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
